import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, getCurrentInstance, onMounted, nextTick } from 'vue';
import {
    LogisticsTopBar,
    StepNavigator,
    DeliveryTransformator,
    DeliveryTable,
    ParsingOverlay,
} from '@/modules/Logistics/components/index';
import {
    LogisticsStep,
    SpreadsheetData,
    SpreadsheetColumn,
    DeliveryListPrintData,
    DeliveryListFormData,
    DeliveryCreatePayload,
    DeliveryUpdatePayload,
    ValidateDeliveryPayload,
    ValidateDeliveryResponse,
    DeliveryMeta,
    DeliveryListItem,
    DeliveryItemUpdatePayload,
} from '@/modules/Logistics/interfaces/LogisticsInterfaces';
import {
    buildSpreadsheetData,
    parseCSVTextGeneric,
    parseServerValidatedData,
    buildPrintSpreadsheetData,
    formatDataForDeliveryAPI,
    buildDeliveryFormData,
    formatDateString,
    inferDayFromDeliveryDate,
    syncSpreadsheetToParsedData,
} from '@/modules/Logistics/composables/LogisticsParserHelper';
import Delivery from '@/modules/Logistics/api/models/Delivery';
import DeliveryItem from '@/modules/Logistics/api/models/DeliveryItem';
import DeliveryRepository from '@/modules/Logistics/api/repositories/DeliveryRepository';
import { formatDateToDateString } from '@/helpers/LocaleHelper';
import { RouteNames } from '@/enums/routes/RouteNames';
import router from '@/router/index';
import Vehicle from '@/modules/Logistics/api/models/Vehicle';
import MontageTeam from '@/modules/Logistics/api/models/MontageTeam';
import VehicleRepository from '@/modules/Logistics/api/repositories/VehicleRepository';
import MontageTeamRepository from '@/modules/Logistics/api/repositories/MontageTeamRepository';

type ParsedData = { data: Record<string, any>[]; columns: SpreadsheetColumn[] };

// ============================================================================
// STATE - Centralized data management
// ============================================================================

// View state
type ProcessAction = 'validate' | 'save' | 'delete' | 'print' | 'save-and-prepare-print';

interface DropdownSelectPayload {
    value: string;
    row: number;
    col: number;
    columnKey: string;
}

/**
 * Handle dropdown selection from SpreadsheetEditor
 * Updates the appropriate cells based on which dropdown was selected
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'Deliveries',
  setup(__props) {

const { proxy } = getCurrentInstance()!;

const showTransformator = ref(false);
const transformatorRef = ref<InstanceType<typeof DeliveryTransformator> | null>(null);
const searchQuery = ref('');

// Transformator state (controlled from parent)
const currentStepIndex = ref(0);
const rawData = ref('');
const parsedData = ref<ParsedData>({ data: [], columns: [] });
const spreadsheetData = ref<SpreadsheetData | null>(null);
const printData = ref<DeliveryListPrintData>({
    spreadsheetData: null,
    formData: {
        deliveryDate: '',
        vehicle: '',
        driver: '',
        departureTime: '',
        arrivalTime: '',
        finalKilometers: '',
        isPrinted: null,
        overallComment: '',
    },
});
const isParsing = ref(false);

// Edit mode state
const editingDeliveryId = ref<string | null>(null);
const isEditMode = computed(() => editingDeliveryId.value !== null);

// Fetched data from BE validation - stores raw response
const serverValidatedData = ref<ValidateDeliveryResponse | null>(null);

// ============================================================================
// COMPUTED
// ============================================================================

const montageTeams = computed(() => {
    return MontageTeamRepository.getAll();
});
const vehicles = computed(() => {
    return VehicleRepository.getAll();
});

const currentDelivery = computed(() => {
    if (!editingDeliveryId.value) {
        return null;
    }
    return DeliveryRepository.getById(editingDeliveryId.value);
});

/**
 * Computed property to calculate total pieces from print spreadsheet data
 * Extracts values from the "quantity" column (index 5) and sums them
 */
const totalPieces = computed(() => {
    const spreadsheet = printData.value.spreadsheetData;
    if (!spreadsheet?.rows) {
        return 0;
    }

    const QUANTITY_COLUMN_INDEX = 5; // 'Kosov' (quantity/pieces) column
    let sum = 0;

    // Iterate through rows (skip header at index 0)
    const rowIndices = Object.keys(spreadsheet.rows)
        .map(Number)
        .filter((idx) => idx > 0);

    for (const rowIdx of rowIndices) {
        const row = spreadsheet.rows[rowIdx];

        // Skip summary row (added by SpreadsheetEditor) — identified by header style (style: 0)
        const quantityCell = row?.cells?.[QUANTITY_COLUMN_INDEX];
        if (quantityCell?.style === 0) {
            continue;
        }

        const cellValue = quantityCell?.text;
        if (cellValue) {
            const parsed = parseInt(cellValue, 10);
            if (!isNaN(parsed)) {
                sum += parsed;
            }
        }
    }

    return sum;
});

/**
 * Check if step can proceed based on current state
 */
function canProceed(stepIndex: number): boolean {
    switch (stepIndex) {
        case 0:
            return rawData.value.trim().length > 0;
        case 1:
            return spreadsheetData.value !== null;
        case 2:
            return printData.value.spreadsheetData !== null;
        default:
            return false;
    }
}

function canGoBack(stepIndex: number): boolean {
    return false;
}

/**
 * Steps configuration - SINGLE SOURCE OF TRUTH
 * Passed as prop to DeliveryTransformator
 */
const transformatorSteps = computed<LogisticsStep[]>(() => [
    {
        id: 0,
        title: 'Import',
        description: 'Paste data',
        isActive: currentStepIndex.value === 0,
        isCompleted: currentStepIndex.value > 0,
        canProceed: canProceed(0),
        proceedButtonText: 'Next',
        class: 'import-step',
        proceedIcon: 'arrow-right',
        action: 'validate',
        showAlternativeButton: false,
        canGoBack: canGoBack(0),
    },
    {
        id: 1,
        title: 'Edit',
        description: 'Review and fix',
        isActive: currentStepIndex.value === 1,
        isCompleted: currentStepIndex.value > 1,
        canProceed: canProceed(1),
        proceedButtonText: 'Next',
        class: 'import-step',
        proceedIcon: 'arrow-right',
        action: 'save-and-prepare-print',
        showAlternativeButton: false,
        canGoBack: canGoBack(1),
    },
    {
        id: 2,
        title: 'Print',
        description: 'Review and print',
        isActive: currentStepIndex.value === 2,
        isCompleted: currentStepIndex.value > 2,
        canProceed: canProceed(2),
        proceedButtonText: 'Print',
        alternativeButtonText: 'Save',
        class: 'import-step',
        proceedIcon: 'print',
        action: 'print',
        alternativeAction: 'save',
        showAlternativeButton: true,
        canGoBack: canGoBack(2),
    },
]);

const currentStep = computed(() => transformatorSteps.value[currentStepIndex.value] || null);

const deliveryListsForTable = computed<DeliveryListItem[]>(() => {
    const deliveries = DeliveryRepository.getAll();
    console.log(deliveries);
    return deliveries.map((delivery: Delivery) => ({
        id: parseInt(delivery.id) || Date.now(),
        date: formatDateToDateString(delivery.date),
        number: String(delivery.id),
        driver: delivery.driver,
        vehicle: delivery.vehicle,
        printed: delivery.isPrinted ? '✅' : '❌',
        rawData: delivery.rawData,
        weekday: inferDayFromDeliveryDate(delivery.date),
    }));
});

const topBarTitle = computed(() => {
    if (showTransformator.value) {
        return isEditMode.value
            ? (proxy.$t('Edit Delivery List') as string)
            : (proxy.$t('New Delivery List') as string);
    }
    return proxy.$t('Deliveries') as string;
});

const topBarSubtitle = computed(() => {
    if (showTransformator.value && transformatorRef.value) {
        return transformatorRef.value.currentStepSubtitle;
    }
    return `${deliveryListsForTable.value.length} ${proxy.$t('delivery lists')}`;
});

const currentUserGroup = computed(() => {
    return String(proxy.$store.getters['jwtData/currentUserGroup']);
});

// ============================================================================
// LIFECYCLE
// ============================================================================

(() => {
    fetchDeliveries();
    fetchDependecies();
})();

// ============================================================================
// API METHODS
// ============================================================================

async function fetchDeliveries() {
    try {
        isParsing.value = true;
        await Delivery.fetchAll();
    } catch (error) {
        console.error('Error fetching deliveries:', error);
        proxy.$notification.error({
            message: proxy.$t('Error') as string,
            description: proxy.$t('Failed to fetch delivery lists.') as string,
        });
    } finally {
        isParsing.value = false;
    }
}

async function fetchDependecies() {
    try {
        await Promise.all([Vehicle.fetchAll(), MontageTeam.fetchAll()]);
    } catch (error) {
        console.error('Error fetching dependencies:', error);
        proxy.$notification.error({
            message: proxy.$t('Error') as string,
            description: proxy.$t('Failed to fetch necessary data.') as string,
        });
    }
}

/**
 * Validate delivery data on backend
 * Returns the raw BE response: { rows, errors, total, parsed, addresses, clients }
 */
async function validateDeliveryData(data: ParsedData): Promise<ValidateDeliveryResponse> {
    const payload: ValidateDeliveryPayload = {
        data: data.data,
    };
    return await Delivery.validate(payload);
}

async function createDelivery(): Promise<Delivery | null> {
    try {
        const payload = buildDeliveryPayload();
        const delivery = await Delivery.createNew(payload);
        proxy.$notification.success({
            message: proxy.$t('Success') as string,
            description: proxy.$t('Delivery list created successfully.') as string,
        });
        return delivery;
    } catch (error) {
        console.error('Error creating delivery:', error);
        proxy.$notification.error({
            message: proxy.$t('Error') as string,
            description: proxy.$t('Failed to create delivery list.') as string,
        });
        return null;
    }
}

async function updateDelivery(): Promise<Delivery | null> {
    if (!editingDeliveryId.value) {
        return null;
    }

    try {
        const payload = buildDeliveryUpdatePayload();

        await updateDeliveryItemsForDelivery(payload.data);

        const delivery = await Delivery.updateExisting(editingDeliveryId.value, payload.meta);

        return delivery;
    } catch (error) {
        console.error('Error updating delivery:', error);
        proxy.$notification.error({
            message: proxy.$t('Error') as string,
            description: proxy.$t('Failed to update delivery list.') as string,
        });
        return null;
    }
}

async function updateDeliveryItemsForDelivery(printRowPayloads: DeliveryItemUpdatePayload[]): Promise<void> {
    if (!editingDeliveryId.value || !currentDelivery?.value?.items?.length) {
        return;
    }

    const existingItems: DeliveryItem[] = currentDelivery.value.items;
    const printRowPositions = new Set(printRowPayloads.map((row) => row.position));

    // Find items to delete: exist in delivery but not in print spreadsheet
    const itemsToDelete = existingItems.filter((item) => !printRowPositions.has(item.position));

    // Find items to update: exist in both
    const itemsToUpdate = printRowPayloads.filter((payload) =>
        existingItems.some((item) => item.position === payload.position),
    );

    try {
        // Delete removed items
        const deletePromises = itemsToDelete.map((item) => DeliveryItem.deleteExisting(item.id));

        // Update existing items
        const updatePromises = itemsToUpdate.map((payload) => {
            const existingItem = existingItems.find((item) => item.position === payload.position)!;
            return DeliveryItem.updateExisting(existingItem.id, payload);
        });

        await Promise.all([...deletePromises, ...updatePromises]);
    } catch (error) {
        console.error('Error updating delivery items:', error);
        throw error;
    }
}

async function processDeletingDeliveries(ids: string[]): Promise<void> {
    try {
        isParsing.value = true;
        await Promise.all(ids.map((id) => Delivery.deleteExisting(id)));
        proxy.$notification.success({
            message: proxy.$t('Success') as string,
            description: `${ids.length} ${proxy.$t('delivery list(s) deleted.') as string}`,
        });
    } catch (error) {
        console.error('Error deleting deliveries:', error);
        proxy.$notification.error({
            message: proxy.$t('Error') as string,
            description: proxy.$t('Failed to delete delivery lists.') as string,
        });
    } finally {
        isParsing.value = false;
    }
}

async function processPrint() {
    if (printData.value.formData) {
        printData.value.formData.isPrinted = true;
    }

    await processSave();
    await printPDFData();

    if (printData.value.formData) {
        printData.value.formData.isPrinted = false;
    }
}

async function printPDFData() {
    if (editingDeliveryId.value) {
        isParsing.value = true;
        try {
            const pdfBlob = await Delivery.getPDF(editingDeliveryId.value);
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.open(pdfUrl, '_blank');
        } catch (e) {
            proxy.$notification.error({
                message: proxy.$t('Error') as string,
                description: proxy.$t('Failed to load PDF') as string,
            });
        } finally {
            isParsing.value = false;
        }
    }
}

function saveDeliveryPrintTable() {
    console.log('saving final...');
}

// ============================================================================
// PAYLOAD BUILDERS
// ============================================================================

/**
 * Build meta object from form fields (header/footer data)
 */
function buildDeliveryMeta(): DeliveryMeta {
    const formData = printData.value.formData;
    const formattedFormData: DeliveryMeta = {
        date: formatDateString(formData?.deliveryDate || ''),
        vehicle: formData?.vehicle || '',
        driver: formData?.driver || '',
        departureTime: formatDateString(formData?.departureTime || ''),
        arrivalTime: formatDateString(formData?.arrivalTime || ''),
        finalKilometers: formData?.finalKilometers ? parseInt(formData.finalKilometers, 10) : null,
        overallComment: formData?.overallComment || '',
    };

    if (formData?.isPrinted === true || formData?.isPrinted === false) {
        formattedFormData.isPrinted = formData.isPrinted;
    }

    return formattedFormData;
}

/**
 * Build payload for creating a new delivery
 * Sends data (parsed rows) and meta (form fields) at the same level
 */
function buildDeliveryPayload(): DeliveryCreatePayload {
    // First update parsedData with spreadsheetData
    // Sync spreadsheet edits back to parsedData
    parsedData.value.data = syncSpreadsheetToParsedData(
        spreadsheetData.value,
        parsedData.value.columns,
        parsedData.value.data,
    );
    // And then return the payload with formatted data for the API
    return {
        data: formatDataForDeliveryAPI(parsedData.value.data),
        meta: buildDeliveryMeta(),
    };
}

/**
 * Build payload for updating an existing delivery
 * Extracts data from print spreadsheet and maps to DeliveryItemUpdatePayload format
 */
function buildDeliveryUpdatePayload(): DeliveryUpdatePayload {
    return {
        data: extractPrintRowsAsItemPayloads(),
        meta: buildDeliveryMeta(),
    };
}

/**
 * Extract rows from print spreadsheet and map to DeliveryItemUpdatePayload format
 * PRINT_COLUMNS: position, clientAddress, additionalNotes, contactNumber, payment, quantity
 */
function extractPrintRowsAsItemPayloads(): DeliveryItemUpdatePayload[] {
    const spreadsheet = printData.value.spreadsheetData;
    if (!spreadsheet?.rows) {
        return [];
    }

    const payloads: DeliveryItemUpdatePayload[] = [];

    // Iterate through rows (skip header at index 0)
    const rowIndices = Object.keys(spreadsheet.rows)
        .map(Number)
        .filter((idx) => idx > 0)
        .sort((a, b) => a - b);

    for (const rowIdx of rowIndices) {
        const row = spreadsheet.rows[rowIdx];
        if (!row?.cells) continue;

        // Extract cell values by column index (matches PRINT_COLUMNS order)
        const position = parseInt(row.cells[0]?.text || '0', 10);
        const clientAddress = row.cells[1]?.text || '';
        const additionalNotes = row.cells[2]?.text || '';
        const contactNumber = row.cells[3]?.text || '';
        const payment = row.cells[4]?.text || '';
        const quantity = parseInt(row.cells[5]?.text || '0', 10);

        // Parse clientAddress: "ClientName, City, Street" format
        const addressParts = clientAddress.split(',').map((s) => s.trim());
        const clientName = addressParts[0] || '';
        const city = addressParts[1] || '';
        const street = addressParts.slice(2).join(', ') || '';

        const payload: DeliveryItemUpdatePayload = {
            position,
            clientName,
            city,
            street,
            notes: additionalNotes || null,
            contactPhone: contactNumber || null,
            payment: payment || null,
            pieces: quantity,
        };

        payloads.push(payload);
    }

    return payloads;
}

// ============================================================================
// PROCESS DATA - Central CRUD handler
// ============================================================================

async function processData(action: ProcessAction, payload?: any): Promise<boolean> {
    switch (action) {
        case 'validate':
            return await processValidation();

        case 'save':
            return await processSave();

        case 'delete':
            if (payload?.ids) {
                await processDeletingDeliveries(payload.ids.map(String));
            }
            return true;

        case 'print':
            await processPrint();
            return true;

        case 'save-and-prepare-print':
            return await saveDeliveryListAndPreparePrintData();

        default:
            return false;
    }
}

async function processValidation(): Promise<boolean> {
    try {
        isParsing.value = true;

        // Parse raw data locally first
        const localParsedData = processRawData();

        if (!localParsedData) {
            return false;
        }

        // Validate on backend (or use existing fetched data for edit mode)

        try {
            // Call BE validation - stores raw response
            const response = await validateDeliveryData(localParsedData);
            serverValidatedData.value = response;

            // Log validation results for debugging
            console.log('Validation response:', {
                total: response.total,
                parsed: response.parsed,
                errors: response.errors,
                addressesCount: response.addresses.length,
                clientsCount: response.clients.length,
            });
        } catch (error) {
            console.error('Validation error:', error);
            // Fallback: use local data without BE validation
            serverValidatedData.value = null;
        }

        // Process fetched data OR fallback to local parsed data
        // parseserverValidatedData handles: extracting rows, building columns with address/client dropdowns
        const parsedValidatedData = parseServerValidatedData(serverValidatedData.value, localParsedData);

        parsedData.value = {
            data: parsedValidatedData.rows,
            columns: parsedValidatedData.columns,
        };

        // Build spreadsheet data
        spreadsheetData.value = buildSpreadsheetData(parsedData.value.data, parsedData.value.columns);

        return true;
    } catch (error) {
        console.error('Error processing data:', error);
        proxy.$notification.error({
            message: proxy.$t('Error') as string,
            description: proxy.$t(
                'An error occurred while processing the data. Please check your input and try again.',
            ) as string,
        });
        return false;
    } finally {
        isParsing.value = false;
    }
}

async function processSave(): Promise<boolean> {
    try {
        isParsing.value = true;

        const delivery = isEditMode.value ? await updateDelivery() : await createDelivery();
        console.log(isParsing.value);
        if (delivery) {
            editingDeliveryId.value = String(delivery.id);
            Delivery.fetchAll(); // Refresh delivery list to update printed status
            return true;
        }

        return false;
    } finally {
        isParsing.value = false;
    }
}

async function saveDeliveryListAndPreparePrintData(): Promise<boolean> {
    if (!parsedData.value.data.length) {
        return false;
    }

    const saved = await processSave();

    if (!saved) {
        return false;
    }

    await nextTick();

    return buildPrintDataForCurrentDelivery();
}

function buildPrintDataForCurrentDelivery(): boolean {
    if (!editingDeliveryId.value) {
        return false;
    }

    if (!currentDelivery.value) {
        return false;
    }

    // Build print-ready spreadsheet data from parsed data
    printData.value.spreadsheetData = buildPrintSpreadsheetData(currentDelivery.value);
    printData.value.formData = buildDeliveryFormData(currentDelivery.value);

    return true;
}

// ============================================================================
// STEP NAVIGATION
// ============================================================================

async function handleNext(action?: string) {
    if (!action) {
        return;
    }
    console.log('running');

    switch (action) {
        case 'validate':
            // Step 0 -> 1: Validate and process data
            const validated = await processData('validate');
            if (validated) {
                currentStepIndex.value = 1;
            }
            break;

        case 'save-and-prepare-print':
            // Step 1 -> 2: Prepare print data
            if (await processData('save-and-prepare-print')) {
                proxy.$notification.success({
                    message: proxy.$t('Success') as string,
                    description: proxy.$t(
                        'Data processing complete! You can now review and print the delivery list.',
                    ) as string,
                });
                currentStepIndex.value = 2;
            }
            break;
        case 'save':
            // Alternative action on Step 2: just save without preparing print data
            await processData('save');
            break;
        case 'print':
            // Step 2: Print
            processData('print');
            break;
    }
}

function handleBack() {
    if (currentStepIndex.value > 0) {
        currentStepIndex.value--;
    }

    if (currentStepIndex.value === 0) {
        resetData();
    }

    if (currentStepIndex.value === 1) {
        resetPrintData();
    }
}

function handleStepClick(step: LogisticsStep, stepIndex: number) {
    return;
    // if (step.isActive) {
    //     return;
    // }

    // if (step.isCompleted) {
    //     currentStepIndex.value = stepIndex;

    //     if (stepIndex === 0) {
    //         resetData();
    //     }
    // }
}

// ============================================================================
// DATA PROCESSING HELPERS
// ============================================================================

function processRawData(): ParsedData | null {
    if (!rawData.value.trim()) {
        return null;
    }

    const result = parseCSVTextGeneric(rawData.value);

    if (!result.success) {
        throw new Error(proxy.$t('No valid data found in the input. Please check the format and try again.') as string);
    }

    return {
        data: result.data,
        columns: result.columns || [],
    };
}

// ============================================================================
// STATE MANAGEMENT
// ============================================================================

function resetData() {
    rawData.value = '';
    parsedData.value = { data: [], columns: [] };
    spreadsheetData.value = null;
    serverValidatedData.value = null;
    resetPrintData();
}

function resetPrintData() {
    printData.value = {
        spreadsheetData: null,
        formData: {
            deliveryDate: '',
            vehicle: '',
            driver: '',
            departureTime: '',
            arrivalTime: '',
            finalKilometers: '',
            isPrinted: null,
            overallComment: '',
        },
    };
}

function resetTransformator() {
    currentStepIndex.value = 0;
    editingDeliveryId.value = null;
    resetData();
}

// ============================================================================
// VIEW HANDLERS
// ============================================================================

function handleNewDeliveryList() {
    resetTransformator();
    showTransformator.value = true;
}

function handleBackToList() {
    showTransformator.value = false;
    resetTransformator();
}

async function handleRowClick(record: DeliveryListItem) {
    editingDeliveryId.value = String(record.id);

    // const selectedDelivery = Delivery.fetchById(String(record.id));

    buildPrintDataForCurrentDelivery();

    showTransformator.value = true;
    currentStepIndex.value = 2;
}

function handleDeleteRows(ids: number[]) {
    processData('delete', { ids });
}

// ============================================================================
// EVENT HANDLERS FROM TRANSFORMATOR
// ============================================================================

function handleDataPasted(data: string) {
    console.log(data);
    rawData.value = data;
}

function handleDataChange(data: SpreadsheetData | null) {
    if (data) {
        spreadsheetData.value = data;
        console.log(data);
    }
}

function handleFormFieldUpdate(
    field: keyof DeliveryListFormData,
    value: DeliveryListFormData[keyof DeliveryListFormData],
) {
    if (printData.value.formData) {
        (printData.value.formData[field] as DeliveryListFormData[typeof field]) = value;
    }
}

function handlePrintSpreadsheetChange(data: SpreadsheetData | null) {
    printData.value.spreadsheetData = data;
}

function handleOpenProject(payload: DropdownSelectPayload) {
    // // Handle open project event
    const cellValue = parsedData.value.data[payload.row - 1]?.[payload.columnKey];
    console.log(cellValue);

    const routeData = router.resolve({
        name: RouteNames.home,
        query: { value: 'Naročila', projectSearchQuery: cellValue, searchType: 'order' },
    });
    window.open(routeData.href, '_blank');
}

function handleDropdownSelect(payload: DropdownSelectPayload) {
    const { value, row, col, columnKey } = payload;

    // Update parsedData to keep in sync
    if (parsedData.value.data[row - 1]) {
        parsedData.value.data[row - 1][columnKey] = value;
    }

    // Handle address dropdown - update Kraj and Naslov columns
    if (columnKey === '_addressDropdown' && value) {
        // Parse "City, Street" format
        const parts = value.split(',').map((s) => s.trim());
        const city = parts[0] || '';
        const street = parts.slice(1).join(',').trim() || '';

        // Find Kraj and Naslov column indices
        const krajColIndex = parsedData.value.columns.findIndex((c) => c.key === 'Kraj');
        const naslovColIndex = parsedData.value.columns.findIndex((c) => c.key === 'Naslov');

        // Update spreadsheet data
        if (spreadsheetData.value?.rows?.[row]) {
            if (krajColIndex >= 0) {
                spreadsheetData.value.rows[row].cells[krajColIndex] = {
                    text: city,
                    style: spreadsheetData.value.rows[row].cells[krajColIndex]?.style || 1,
                };
                if (parsedData.value.data[row - 1]) {
                    parsedData.value.data[row - 1]['Kraj'] = city;
                }
            }
            if (naslovColIndex >= 0) {
                spreadsheetData.value.rows[row].cells[naslovColIndex] = {
                    text: street,
                    style: spreadsheetData.value.rows[row].cells[naslovColIndex]?.style || 1,
                };
                if (parsedData.value.data[row - 1]) {
                    parsedData.value.data[row - 1]['Naslov'] = street;
                }
            }
        }
    }

    // Handle client dropdown - update Stranka column
    if (columnKey === '_clientDropdown' && value) {
        const strankaColIndex = parsedData.value.columns.findIndex((c) => c.key === 'Stranka');

        if (spreadsheetData.value?.rows?.[row] && strankaColIndex >= 0) {
            spreadsheetData.value.rows[row].cells[strankaColIndex] = {
                text: value,
                style: spreadsheetData.value.rows[row].cells[strankaColIndex]?.style || 1,
            };
            if (parsedData.value.data[row - 1]) {
                parsedData.value.data[row - 1]['Stranka'] = value;
            }
        }
    }

    // Reload spreadsheet to reflect changes
    if (spreadsheetData.value) {
        transformatorRef.value?.reloadSpreadsheetData(spreadsheetData.value);
    }
}

return { __sfc: true,proxy, showTransformator, transformatorRef, searchQuery, currentStepIndex, rawData, parsedData, spreadsheetData, printData, isParsing, editingDeliveryId, isEditMode, serverValidatedData, montageTeams, vehicles, currentDelivery, totalPieces, canProceed, canGoBack, transformatorSteps, currentStep, deliveryListsForTable, topBarTitle, topBarSubtitle, currentUserGroup, fetchDeliveries, fetchDependecies, validateDeliveryData, createDelivery, updateDelivery, updateDeliveryItemsForDelivery, processDeletingDeliveries, processPrint, printPDFData, saveDeliveryPrintTable, buildDeliveryMeta, buildDeliveryPayload, buildDeliveryUpdatePayload, extractPrintRowsAsItemPayloads, processData, processValidation, processSave, saveDeliveryListAndPreparePrintData, buildPrintDataForCurrentDelivery, handleNext, handleBack, handleStepClick, processRawData, resetData, resetPrintData, resetTransformator, handleNewDeliveryList, handleBackToList, handleRowClick, handleDeleteRows, handleDataPasted, handleDataChange, handleFormFieldUpdate, handlePrintSpreadsheetChange, handleOpenProject, handleDropdownSelect, LogisticsTopBar, StepNavigator, DeliveryTransformator, DeliveryTable, ParsingOverlay }
}

})